import { useRef, useMemo, useCallback, type ChangeEvent } from 'react';
import { DiagramFileInfo } from '../DiagramFileInfo';
import { DiagramSummaryItem } from '../DiagramSummaryItem';
import { DataSummary } from '../common/DataSummary';
import { EMPTY_DIAGRAM_FILE_SETTINGS } from '../../types/diagram.types';
import type { DiagramInfo } from '../../types/diagram.types';
import type { DiagramPreviewProps } from './types';

export const DiagramPreview = ({
  diagramData,
  projectId,
  diagramFileSettingsMap,
  onPageImageChange,
  onCompressionSettingsChange,
  onPdfChange,
  name,
  onNameChange,
  onFolderSelect,
}: DiagramPreviewProps) => {
  const folderInputRef = useRef<HTMLInputElement>(null);

  const handleFolderInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        onFolderSelect(files);
      }
      // Reset so the same folder can be re-selected
      event.target.value = '';
    },
    [onFolderSelect],
  );
  const diagramInfoMap = useMemo(() => {
    const map = new Map<string, DiagramInfo>();
    for (const item of diagramData) {
      for (const d of item.diagrams || []) {
        const key = typeof d.diagram === 'string' ? d.diagram : d.diagram.id;
        const name = typeof d.diagram === 'string' ? d.diagram : d.diagram.name;
        if (!map.has(key)) {
          map.set(key, {
            name,
            pages: new Set(),
            itemCount: 0,
            occurrenceCount: 0,
          });
        }
        const info = map.get(key)!;
        info.itemCount++;
        for (const p of d.pages || []) {
          info.pages.add(p.page);
          info.occurrenceCount++;
        }
      }
    }
    return map;
  }, [diagramData]);

  const totalPages = new Set<string>();
  diagramInfoMap.forEach((info) =>
    info.pages.forEach((p) => totalPages.add(p)),
  );
  let totalOccurrences = 0;
  diagramInfoMap.forEach((info) => (totalOccurrences += info.occurrenceCount));
  let totalItemTypes = 0;
  diagramInfoMap.forEach((info) => (totalItemTypes += info.itemCount));
  const lvMatchCount = diagramData.filter((item) => item.lvMatch?.lvId).length;

  return (
    <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
      <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
        Preview — Diagram Data
      </h2>
      <DiagramFileInfo name={name} onNameChange={onNameChange} />
      <div className="bg-[#0d1117] border border-[#30363d] rounded-md p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm text-[#c9d1d9] font-semibold">Diagrams</h3>
          <div>
            <input
              ref={folderInputRef}
              type="file"
              /* @ts-expect-error webkitdirectory is not in standard typings */
              webkitdirectory=""
              directory=""
              multiple
              className="hidden"
              onChange={handleFolderInputChange}
            />
            <button
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-[#1f6feb] hover:opacity-85 cursor-pointer"
              onClick={() => folderInputRef.current?.click()}
            >
              Select Images Folder
            </button>
          </div>
        </div>
        {[...diagramInfoMap.entries()].map(([key, info]) => (
          <DiagramSummaryItem
            key={key}
            diagramKey={key}
            info={info}
            projectId={projectId}
            fileSettings={
              diagramFileSettingsMap[key] ?? EMPTY_DIAGRAM_FILE_SETTINGS
            }
            onPageImageChange={onPageImageChange}
            onCompressionSettingsChange={onCompressionSettingsChange}
            onPdfChange={onPdfChange}
          />
        ))}
      </div>
      <div className="bg-[#0d1117] border border-[#30363d] rounded-md p-3">
        <h3 className="text-sm text-[#c9d1d9] font-semibold mb-2">
          Data Summary
        </h3>
        <DataSummary
          items={[
            { label: `${diagramInfoMap.size} diagram(s)`, variant: 'green' },
            {
              label: `${totalPages.size} unique page(s)`,
              variant: 'green',
            },
            {
              label: `${diagramData.length} JSON item(s)`,
              variant: 'green',
            },
            { label: `${totalItemTypes} item type row(s)`, variant: 'green' },
            {
              label: `${totalOccurrences} occurrence row(s)`,
              variant: 'green',
            },
            { label: `${lvMatchCount} with LV match` },
          ]}
        />
      </div>
    </div>
  );
};
